// The §13.1 bus graph. Works on any BaseAudioContext, so the offline mix checks render exactly
// the live chain.
//
//   SFX (far, near +2 dB drier) ─┐
//   Voice ───────────────────────┤
//   Crowd ───────────────────────┼─► pre-master ─► −3 dB ─► compressor (−18 dB, 4:1, 3/250 ms) ─► +2.5 dB
//   Music ─► teeter LP ─► mood LP ─► duck ─┤        ─► limiter (−3 dB, 20:1, 1 ms) ─► master ─► safety ─► out
//   Stingers ────────────────────┤
//   UI ──────────────────────────┘
//   Ghost ─► LP 2.2 kHz ─► SFX (dry) + 50 % to the reverb
//   reverb send ─► Convolver (per-map IR) ─► pre-master
//
// The safety stage is a WaveShaper that is linear below −3 dBFS and can never exceed −0.5 dBFS:
// whatever the compressors let through, the output stays under it (§29.4: limiter peak ≤ −0.3 dBFS).
import type { MapId } from '@shared/ids';
import type { Settings } from '../profile/settings';
import { generateIR, REVERBS } from './reverb';
import { dbToGain, volumeToGain } from './util';

export type Volumes = Settings['audio'];

/** Mix constants (presentation-only tuning, §17.2 boundary). */
/** §14.4's default SFX volume: the ghost bus's wet send was tuned at it. */
const DEFAULT_SFX_VOLUME = 80;

export const MIX = {
  /** Bus trims, dB, before the user volume. */
  sfx: -2, voice: -2.5, crowd: -2, music: -7, stingers: -2, ui: 4,
  /** Reverb sends (linear). */
  sendFar: 0.24, sendNear: 0.08, sendVoice: 0.18, sendCrowd: 0.4, sendMusic: 0.07, ghostWet: 0.5,
  nearDb: 2,
  ghostLowpass: 2200,
  /**
   * Pre-compressor trim (Web Audio compressors add make-up gain), dB. The bus compressor glues
   * (≈ 1–3 dB in a busy round) instead of squashing every bonk; the explicit ducks carry the
   * big moments. The trim is given back as drive into the peak limiter.
   */
  preCompDb: -3,
  limiterDriveDb: 2.5,
  /** Safety ceiling, dBFS. */
  ceilingDb: -0.5,
  /** Ducking ramps, seconds. */
  duckAttack: 0.025, duckRelease: 0.12,
} as const;

/**
 * The safety stage's transfer curve: exactly linear below the knee (−3 dBFS); above it a tanh
 * shoulder with unit slope at the knee that approaches `ceiling` and can never exceed it. It never
 * adds gain (tanh(u) ≤ u) and its slope never exceeds 1, so it only ever rounds off stray peaks.
 */
export function safetyCurve(ceilingDb: number, n = 4096, kneeDb = -3): Float32Array<ArrayBuffer> {
  const ceiling = dbToGain(ceilingDb);
  const knee = Math.min(dbToGain(kneeDb), ceiling * 0.9);
  const span = ceiling - knee;
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const a = Math.abs(x);
    const y = a <= knee ? a : knee + span * Math.tanh((a - knee) / span);
    c[i] = Math.sign(x) * Math.min(ceiling, y);
  }
  return c;
}

interface Duck { db: number; from: number; until: number }

export class Mixer {
  readonly sfxFar: GainNode;
  readonly sfxNear: GainNode;
  readonly ghost: GainNode;
  readonly voice: GainNode;
  readonly crowd: GainNode;
  readonly music: GainNode;
  readonly stingers: GainNode;
  readonly ui: GainNode;
  readonly analyser: AnalyserNode;
  readonly output: AudioNode;

  private readonly sfxFarVol: GainNode;
  private readonly ghostWetVol: GainNode;
  private readonly sfxNearVol: GainNode;
  private readonly voiceVol: GainNode;
  private readonly crowdVol: GainNode;
  private readonly musicVol: GainNode;
  private readonly stingerVol: GainNode;
  private readonly uiVol: GainNode;
  private readonly master: GainNode;
  readonly teeterLp: BiquadFilterNode;
  readonly moodLp: BiquadFilterNode;
  private readonly duckGain: GainNode;
  private readonly convolver: ConvolverNode;
  private readonly comp: DynamicsCompressorNode;
  private readonly limiter: DynamicsCompressorNode;
  private readonly irCache = new Map<MapId, AudioBuffer>();
  private reverbMap: MapId | null = null;
  private ducks: Duck[] = [];
  private duckDb = 0;
  private moodHz = 20000;

  constructor(readonly ctx: BaseAudioContext, destination: AudioNode = ctx.destination) {
    const g = (v = 1): GainNode => {
      const n = ctx.createGain();
      n.gain.value = v;
      return n;
    };
    const preMaster = g(1);
    const reverbIn = g(1);
    this.convolver = ctx.createConvolver();
    this.convolver.normalize = false;
    const reverbReturn = g(0.55);
    reverbIn.connect(this.convolver).connect(reverbReturn).connect(preMaster);

    // SFX: far (others) and near (the local bean: +2 dB, drier).
    this.sfxFar = g();
    this.sfxNear = g(dbToGain(MIX.nearDb));
    this.sfxFarVol = g();
    this.sfxNearVol = g();
    this.sfxFar.connect(this.sfxFarVol).connect(preMaster);
    this.sfxNear.connect(this.sfxNearVol).connect(preMaster);
    this.sfxFarVol.connect(g(MIX.sendFar)).connect(reverbIn);
    this.sfxNearVol.connect(g(MIX.sendNear)).connect(reverbIn);

    // Ghost: low-passed "from beyond", half wet, into the SFX bus.
    this.ghost = g();
    const ghostLp = ctx.createBiquadFilter();
    ghostLp.type = 'lowpass';
    ghostLp.frequency.value = MIX.ghostLowpass;
    ghostLp.Q.value = 0.9;
    this.ghost.connect(ghostLp);
    ghostLp.connect(this.sfxFar);
    // The wet half follows the SFX volume too (it bypassed it: ghost reverb at SFX 0).
    this.ghostWetVol = g();
    ghostLp.connect(g(MIX.ghostWet)).connect(this.ghostWetVol).connect(reverbIn);

    this.voice = g();
    this.voiceVol = g();
    this.voice.connect(this.voiceVol).connect(preMaster);
    this.voiceVol.connect(g(MIX.sendVoice)).connect(reverbIn);

    this.crowd = g();
    this.crowdVol = g();
    this.crowd.connect(this.crowdVol).connect(preMaster);
    this.crowdVol.connect(g(MIX.sendCrowd)).connect(reverbIn);

    // Music: teeter LP (fast automation), mood LP (potato / ghost), ducking.
    this.music = g();
    this.teeterLp = ctx.createBiquadFilter();
    this.teeterLp.type = 'lowpass';
    this.teeterLp.frequency.value = 20000;
    this.teeterLp.Q.value = 0.9;
    this.moodLp = ctx.createBiquadFilter();
    this.moodLp.type = 'lowpass';
    this.moodLp.frequency.value = 20000;
    this.moodLp.Q.value = 0.8;
    this.duckGain = g();
    this.musicVol = g();
    this.music.connect(this.teeterLp).connect(this.moodLp).connect(this.duckGain).connect(this.musicVol).connect(preMaster);
    this.musicVol.connect(g(MIX.sendMusic)).connect(reverbIn);
    // Stingers: musical, but never ducked or filtered.
    this.stingers = g();
    this.stingerVol = g();
    this.stingers.connect(this.stingerVol).connect(preMaster);
    this.stingerVol.connect(g(MIX.sendMusic * 2)).connect(reverbIn);

    this.ui = g();
    this.uiVol = g();
    this.ui.connect(this.uiVol).connect(preMaster);

    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 4;
    comp.knee.value = 6;
    comp.attack.value = 0.003;
    comp.release.value = 0.25;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -3;
    limiter.ratio.value = 20;
    limiter.knee.value = 0;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;
    // The compressors' automatic make-up gain is folded into this trim (headroom for the safety).
    const preComp = g(dbToGain(MIX.preCompDb));
    this.master = g();
    const safety = ctx.createWaveShaper();
    safety.curve = safetyCurve(MIX.ceilingDb);
    safety.oversample = 'none';
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    const drive = g(dbToGain(MIX.limiterDriveDb));
    preMaster.connect(preComp).connect(comp).connect(drive).connect(limiter).connect(this.master).connect(safety);
    this.comp = comp;
    this.limiter = limiter;
    safety.connect(destination);
    safety.connect(this.analyser);
    this.output = safety;
    this.setVolumes({ master: 80, music: 70, sfx: 80, voices: 80, crowd: 70, muteWhenHidden: true });
  }

  setVolumes(v: Volumes): void {
    const now = this.ctx.currentTime;
    const set = (node: GainNode, value: number): void => {
      node.gain.cancelScheduledValues(now);
      node.gain.setTargetAtTime(value, now, 0.03);
    };
    const sfx = volumeToGain(v.sfx) * dbToGain(MIX.sfx);
    set(this.sfxFarVol, sfx);
    set(this.sfxNearVol, sfx);
    // Unity at the default SFX volume (the ghost's tuned wet level), scaling with the slider.
    set(this.ghostWetVol, volumeToGain(v.sfx) / volumeToGain(DEFAULT_SFX_VOLUME));
    set(this.uiVol, volumeToGain(v.sfx) * dbToGain(MIX.ui));
    set(this.voiceVol, volumeToGain(v.voices) * dbToGain(MIX.voice));
    set(this.crowdVol, volumeToGain(v.crowd) * dbToGain(MIX.crowd));
    set(this.musicVol, volumeToGain(v.music) * dbToGain(MIX.music));
    set(this.stingerVol, volumeToGain(v.music) * dbToGain(MIX.stingers));
    set(this.master, volumeToGain(v.master));
  }

  /** Builds (once per map) and selects the map's reverb. */
  setReverb(mapId: MapId): void {
    if (this.reverbMap === mapId) return;
    this.reverbMap = mapId;
    let buf = this.irCache.get(mapId);
    if (!buf) {
      const sr = this.ctx.sampleRate;
      const [l, r] = generateIR(REVERBS[mapId], sr, mapId + 1);
      buf = new AudioBuffer({ length: l.length, sampleRate: sr, numberOfChannels: 2 });
      buf.copyToChannel(l, 0);
      buf.copyToChannel(r, 1);
      this.irCache.set(mapId, buf);
    }
    this.convolver.buffer = buf;
  }

  /** Music ducking: `db` (negative) for `holdSec` (Infinity until `unduck(db)`), from `at`. */
  duck(db: number, holdSec: number, at = this.ctx.currentTime): void {
    this.ducks.push({ db, from: at, until: at + holdSec });
    this.applyDuck();
  }

  unduck(db: number): void {
    this.ducks = this.ducks.filter((d) => d.db !== db || Number.isFinite(d.until));
    this.applyDuck();
  }

  /** Per frame (and scheduler tick): starts and expires ducks. */
  update(): void {
    const now = this.ctx.currentTime;
    if (this.ducks.some((d) => d.until <= now)) this.ducks = this.ducks.filter((d) => d.until > now);
    this.applyDuck();
  }

  private applyDuck(): void {
    const now = this.ctx.currentTime;
    const target = this.ducks.reduce((m, d) => (d.from <= now + 0.01 && d.until > now ? Math.min(m, d.db) : m), 0);
    if (target === this.duckDb) return;
    const attack = target < this.duckDb;
    this.duckDb = target;
    this.duckGain.gain.cancelScheduledValues(now);
    this.duckGain.gain.setTargetAtTime(dbToGain(target), now, attack ? MIX.duckAttack : MIX.duckRelease);
  }

  /** Current gain reduction of the bus compressor and the limiter, dB (diagnostics). */
  get reduction(): { comp: number; limiter: number } {
    return { comp: this.comp.reduction, limiter: this.limiter.reduction };
  }

  get duckingDb(): number {
    return this.duckDb;
  }

  /** §13.4 teeter: dip to 700 Hz over 150 ms. */
  teeterDip(): void {
    const p = this.teeterLp.frequency;
    const now = this.ctx.currentTime;
    p.cancelScheduledValues(now);
    p.setValueAtTime(Math.max(700, p.value), now);
    p.exponentialRampToValueAtTime(700, now + 0.15);
  }

  /** The save: snap back over 60 ms (or ease back over `sec`). */
  teeterRelease(sec = 0.06): void {
    const p = this.teeterLp.frequency;
    const now = this.ctx.currentTime;
    p.cancelScheduledValues(now);
    p.setValueAtTime(Math.max(20, Math.min(20000, p.value)), now);
    p.exponentialRampToValueAtTime(20000, now + sec);
  }

  /** Potato / ghost cutoff (the lower of the two), smoothed. */
  setMoodCutoff(hz: number): void {
    const v = Math.max(200, Math.min(20000, hz));
    if (Math.abs(v - this.moodHz) < 5) return;
    this.moodHz = v;
    const now = this.ctx.currentTime;
    this.moodLp.frequency.cancelScheduledValues(now);
    this.moodLp.frequency.setTargetAtTime(v, now, 0.08);
  }

  /** Brings the music bus back (from silence) at `at`. */
  musicRestore(at: number): void {
    const p = this.music.gain;
    p.cancelScheduledValues(at);
    p.setValueAtTime(p.value > 0.99 ? 1 : 0, at);
    p.linearRampToValueAtTime(1, at + 0.04);
  }

  /** Final-KO tape stop: the music bus fades to `to` over `sec`. */
  musicFade(to: number, sec: number, at = this.ctx.currentTime): void {
    const p = this.music.gain;
    p.cancelScheduledValues(at);
    p.setValueAtTime(p.value, at);
    p.linearRampToValueAtTime(to, at + sec);
  }
}
